
  // Own global instead of the shared `countdownTimer`: `countdown-timer.liquid`
  // and `countdown-timer-v2.liquid` assign that name unconditionally, so on a
  // page rendering both, whichever ran last would win and the evergreen mode
  // would silently fall back to the fixed-date branch.
  window.rdmAnnouncementCountdownV3 =
    window.rdmAnnouncementCountdownV3 ||
    ((config) => ({
      endDate: null,
      finishedMessage: config.finishedMessage,
      finished: false,
      days: 0,
      hours: 0,
      totalHours: 0,
      minutes: 0,
      seconds: 0,
      intervalId: null,
      timeoutId: null,
      countUp: false,
      countUpBase: 0,
      recurring: false,
      recurringStartMs: 0,
      resetDurationMs: 0,
      recurringCountsUp: false,

      init() {
        if (config.mode === 'evergreen') {
          this.initEvergreen();
          return;
        }

        if (config.mode === 'countup') {
          this.initCountUp();
          return;
        }

        if (config.mode === 'recurring') {
          this.initRecurring();
          return;
        }

        try {
          this.endDate = this._parseDateTimeInZone(
            config.endDateString,
            config.timeZone
          );
        } catch (e) {
          this.finished = true;
          return;
        }
        this.startCountdown();
      },

      // Evergreen: the deadline is stored per visitor in localStorage, so it
      // survives page loads and closed tabs. Editing the configured duration
      // writes a new deadline instead of resuming the stale one.
      initEvergreen() {
        const durationMs = config.durationMs;
        if (!durationMs || durationMs < 0) {
          this.finished = true;
          return;
        }

        let start = null;
        try {
          const stored = JSON.parse(localStorage.getItem(config.storageKey));
          if (
            stored &&
            stored.duration === durationMs &&
            typeof stored.start === 'number'
          ) {
            start = stored.start;
          }
        } catch (e) {
          start = null;
        }

        if (start === null) {
          start = Date.now();
          try {
            localStorage.setItem(
              config.storageKey,
              JSON.stringify({ start: start, duration: durationMs })
            );
          } catch (e) {
            // Private mode / storage disabled: fall back to this page view only.
          }
        }

        this.endDate = new Date(start + durationMs);
        this.startCountdown();
      },

      // Count up: the configured value is the number shown on the visitor's
      // first view, and it grows from there. Storing when that first view
      // happened (per visitor, in localStorage) keeps the number increasing
      // across page loads instead of snapping back to the start value.
      // Editing the configured start writes a new anchor.
      initCountUp() {
        const startMs = config.countUpStartMs || 0;

        let firstSeen = null;
        try {
          const stored = JSON.parse(
            localStorage.getItem(config.countUpStorageKey)
          );
          if (
            stored &&
            stored.offset === startMs &&
            typeof stored.start === 'number'
          ) {
            firstSeen = stored.start;
          }
        } catch (e) {
          firstSeen = null;
        }

        if (firstSeen === null) {
          firstSeen = Date.now();
          try {
            localStorage.setItem(
              config.countUpStorageKey,
              JSON.stringify({ start: firstSeen, offset: startMs })
            );
          } catch (e) {
            // Private mode / storage disabled: fall back to this page view only.
          }
        }

        // Virtual "zero" moment: elapsed time from it already equals the
        // configured start value at the visitor's first view.
        this.countUpBase = firstSeen - startMs;
        this.countUp = true;
        this.startCountdown();
      },

      // Recurring: same cycle for every visitor. Its phase is based on the
      // shared Unix epoch rather than a visitor or a configured date, so it is
      // synchronized everywhere and is not stored per visitor.
      initRecurring() {
        const resetDurationMs = config.recurringResetMs;
        if (!resetDurationMs || resetDurationMs <= 0) {
          this.finished = true;
          return;
        }

        this.recurring = true;
        this.recurringStartMs = config.recurringStartMs || 0;
        this.resetDurationMs = resetDurationMs;
        this.recurringCountsUp = config.recurringDirection === 'countup';
        this.startCountdown();
      },

      _parseDateTimeInZone(dateTimeString, timeZone) {
        const [datePart, timePart] = dateTimeString.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        const targetDate = new Date(
          Date.UTC(year, month - 1, day, hours, minutes, seconds)
        );
        const targetTimeZoneOffsetMs = this._getTimeZoneOffsetMs(
          targetDate,
          timeZone
        );
        const localOffsetMs = targetDate.getTimezoneOffset() * 60 * 1000;
        return new Date(
          targetDate.getTime() + localOffsetMs + targetTimeZoneOffsetMs
        );
      },

      _getTimeZoneOffsetMs(date, timeZone) {
        const dateInTimeZone = new Date(
          date.toLocaleString('en-US', { timeZone: timeZone })
        );
        const utcDate = new Date(
          date.toLocaleString('en-US', { timeZone: 'UTC' })
        );
        return dateInTimeZone.getTime() - utcDate.getTime();
      },

      startCountdown() {
        clearInterval(this.intervalId);
        clearTimeout(this.timeoutId);
        this.updateCountdown();
        const startInterval = () => {
          this.updateCountdown();
          this.intervalId = setInterval(() => {
            this.updateCountdown();
          }, 1000);
        };
        this.timeoutId = setTimeout(startInterval, 1000 - (Date.now() % 1000));
      },

      destroy() {
        clearInterval(this.intervalId);
        clearTimeout(this.timeoutId);
      },

      updateCountdown() {
        const now = new Date().getTime();
        let distance;
        if (this.recurring) {
          const elapsed =
            ((now % this.resetDurationMs) +
              this.resetDurationMs) %
            this.resetDurationMs;
          distance = this.recurringCountsUp
            ? this.recurringStartMs + elapsed
            : Math.max(0, this.recurringStartMs - elapsed);
        } else if (this.countUp) {
          distance = now - this.countUpBase;
        } else {
          distance = this.endDate.getTime() - now;
        }
        if (distance < 0) {
          clearInterval(this.intervalId);
          this.finished = true;
          return;
        }
        this.days = Math.floor(distance / (1000 * 60 * 60 * 24));
        this.hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        this.totalHours = Math.floor(distance / (1000 * 60 * 60));
        this.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        this.seconds = Math.floor((distance % (1000 * 60)) / 1000);
      },

      formatTime(value) {
        return String(value).padStart(2, '0');
      },
    }));

/* Isolated reference presentation. All purchase/contact actions remain local. */
(() => {
  const notice = message => {document.querySelector('#reference-notice-text').textContent=message;document.querySelector('#reference-notice').showModal();};
  window.addEventListener('message',e=>{if(e.source===document.querySelector('#iframe-frontrow-quant')?.contentWindow&&e.data==='reference-frontrow')notice('FrontrowMD is an external clinician service. This reference preserves its badge; the external service is isolated in this preview.');});
  const add=()=>window.MilkOfferCart?.buy();
  document.addEventListener('alpine:init',()=>{
    Alpine.store('scroll',{scrollTo(id){document.getElementById(id)?.scrollIntoView({behavior:'smooth'});}});
    Alpine.store('cart',{quarterlyOfferPopupIsOpen:false,showDrawer:false});
    Alpine.data('button',()=>({cooldown:false,handleButtonClick(){add();},addToCart(){add();},addToCartQuarterlyOffet(){add();},addToCartKit(){add();}}));
    Alpine.data('heightSync',()=>({height:0,init(){this.height=this.$el.offsetHeight;}}));
    Alpine.data('slider',()=>({
      instance:null,
      sliderInit(el,options={},events={},thumbSelector){
        if(el.swiper)return;
        const container=el.closest('.swiper-outer')||el;
        const config={...options,observer:true,observeParents:true,watchSlidesProgress:true};
        const prev=el.querySelector('.component-slider__prev')||container.querySelector('.component-slider__prev-outer,.component-slider__prev.component-slider__nav-manual');
        const next=el.querySelector('.component-slider__next')||container.querySelector('.component-slider__next-outer,.component-slider__next.component-slider__nav-manual');
        if(prev||next)config.navigation={prevEl:prev,nextEl:next};
        const pagination=el.querySelector('.component-slider__pagination');
        if(pagination)config.pagination={el:pagination,clickable:true,bulletActiveClass:'active',renderBullet:(i,c)=>'<span class="component-slider__pagination-item flex w-[11px] h-[11px] rounded-full bg-[#404040] [&.active]:bg-white hover:bg-white transition-all duration-300 border-0 outline-0 cursor-pointer [&>*]:pointer-events-none text-0 text-transparent leading-0 '+c+'" role="button" aria-label="Go to slide '+(i+1)+'"></span>'};
        if(options.slidesOffsetBefore){
          const responsive={...options};Object.keys(options.breakpoints||{}).map(Number).sort((a,b)=>a-b).forEach(w=>{if(innerWidth>=w)Object.assign(responsive,options.breakpoints[w]);});
          config.width=el.clientWidth-(responsive.slidesOffsetBefore||0)-(responsive.slidesOffsetAfter||0);
        }
        const instance=new Swiper(el,config); this.instance=instance;el.classList.add('slider-active');
        if(thumbSelector){
          const thumbs=container.querySelector(thumbSelector);
          if(thumbs){
            const slides=[...thumbs.querySelectorAll('.swiper-slide')];
            if(!thumbs.swiper)new Swiper(thumbs,{slidesPerView:5,spaceBetween:8,watchSlidesProgress:true});
            slides.forEach((slide,i)=>slide.addEventListener('click',()=>instance.params.loop?instance.slideToLoop(i):instance.slideTo(i)));
            const sync=()=>{slides.forEach((slide,i)=>slide.classList.toggle('swiper-slide-thumb-active',i===instance.realIndex));thumbs.swiper?.slideTo(Math.max(0,instance.realIndex-3));};
            instance.on('slideChange',sync);sync();
            const wrap=thumbs.parentElement;
            wrap.querySelectorAll('button').forEach((b,i)=>b.addEventListener('click',()=>i?instance.slideNext():instance.slidePrev()));
          }
        }
      },destroy(){this.instance?.destroy(true,true);}
    }));
  });
  document.addEventListener('click',e=>{
    const menuSummary=e.target.closest('#Details-menu-drawer-container > summary');
    if(menuSummary){e.preventDefault();const d=menuSummary.parentElement;const open=!d.open;d.open=open;d.classList.toggle('menu-opening',open);menuSummary.setAttribute('aria-expanded',String(open));document.body.style.setProperty('overflow-y',open?'hidden':'visible','important');return;}
    if(e.target.closest('.close-mobile-menu-wraper')){const d=document.querySelector('#Details-menu-drawer-container');d.open=false;d.classList.remove('menu-opening');d.querySelector('summary').setAttribute('aria-expanded','false');document.body.style.setProperty('overflow-y','visible','important');return;}
    const close=e.target.closest('[data-preview-close]');if(close){close.closest('dialog').close();return;}
    const b=e.target.closest('button');
    if(b?.getAttribute('aria-label')?.includes('Truemed')){e.preventDefault();notice('Truemed is an external HSA/FSA qualification service. Eligibility and payment are isolated in this reference preview.');return;}
    const a=e.target.closest('a');
    if(a){const href=a.getAttribute('href')||'';
      if(/\/products\/30-day-supply-starter-kit/.test(href)){e.preventDefault();add();return;}
      if(href.startsWith('/')||href.startsWith('mailto:')||href.startsWith('https://www.getdetoxd.com')||href.startsWith('https://getdetoxd.com'))return;
      if(href.startsWith('http')){e.preventDefault();notice('Reference-only destination: '+href+'. External navigation is isolated in this preview.');return;}
    }
  },true);
  document.addEventListener('submit',e=>{e.preventDefault();notice('This form is a reference preview. Nothing was submitted.');},true);
  document.addEventListener('DOMContentLoaded',()=>{
    // Preserve source sticky header measurements without source global scripts.
    const header=document.querySelector('sticky-header');
    const measure=()=>{if(header)document.documentElement.style.setProperty('--header-height',header.offsetHeight+'px');};measure();window.addEventListener('resize',measure);
    document.querySelectorAll('iframe[data-preview-widget]').forEach(x=>{
      x.srcdoc='<html><head><style>body{margin:0;background:#000;color:#fff;text-align:center;font:14px Arial}p{margin:5px}button{background:none;border:0;color:white;text-decoration:underline;font-size:14px}</style></head><body><p style="font:20px Georgia">❧ Clinicians\' Choice ❧</p><p><b>294 clinicians</b> share this on <i>FrontrowMD</i> without compensation.</p><button>View clinicians &amp; learn more</button></body></html>';
    });
  });
})();

// Source presentation-only footer and sticky CTA, without experiment tracking.
document.addEventListener('DOMContentLoaded',()=>{
 const first=document.querySelector('.footer__nav-block');first?.classList.add('is-open');first?.querySelector('[role="button"]')?.setAttribute('aria-expanded','true');
 document.querySelectorAll('.footer__nav-block-toggle').forEach(b=>{const toggle=()=>{const open=b.parentElement.classList.toggle('is-open');b.setAttribute('aria-expanded',String(open));};b.addEventListener('click',toggle);b.addEventListener('keydown',e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();toggle();}});});
 const bar=document.querySelector('#mm-sticky-cta');let suppressed=false,anchor=null;
 const update=()=>{if(!bar)return;if(suppressed&&anchor!==null&&Math.abs(scrollY-anchor)>150)suppressed=false;const cta=document.querySelector('#rdm-main-pdp-section button.rdm-btn-primary');const r=cta?.getBoundingClientRect();const inView=r&&r.bottom>0&&r.top<innerHeight-bar.offsetHeight-8;const show=!suppressed&&scrollY>=3*innerHeight&&!inView&&getComputedStyle(document.body).overflowY!=='hidden';bar.classList.toggle('mm-visible',show);bar.setAttribute('aria-hidden',String(!show));};
 bar?.querySelector('a')?.addEventListener('click',e=>{e.preventDefault();document.querySelector('#rdm-main-pdp-section')?.scrollIntoView({behavior:'smooth'});suppressed=true;anchor=null;update();setTimeout(()=>anchor=scrollY,1200);});window.addEventListener('scroll',update,{passive:true});window.addEventListener('resize',update);setInterval(update,500);update();
});

document.addEventListener('DOMContentLoaded',()=>{
 document.querySelectorAll('a[href*="mengotomars.com"]').forEach(a=>{
  const url=new URL(a.href);
  if(a.id==='cart-icon-bubble'){a.href='/cart';}
  else if(url.pathname.startsWith('/products/')){a.href='#rdm-main-pdp-section';}
  else if(url.pathname==='/'){a.href='/';}
  else if(url.pathname.startsWith('/account')){a.href='/account/login';}
  else{a.href='#rdm-getting-results';}
 });
});
